import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Showcase = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [slideDirection, setSlideDirection] = useState('next'); // 'next' or 'prev'
  const intervalRef = useRef(null);

  useEffect(() => {
    const importImages = async () => {
      try {
        const res = await fetch('/images.json');
        const data = await res.json();
        setImages(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading images:', error);
        setLoading(false);
      }
    };

    importImages();
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && images.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 5000); // 5 seconds - slower for better performance
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, images.length]);

  // Navigation functions
  const goToPrevious = useCallback(() => {
    setSlideDirection('prev');
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    setIsAutoPlaying(false);
    // Resume auto-play after 3 seconds
    setTimeout(() => setIsAutoPlaying(true), 3000);
  }, [images.length]);

  const goToNext = useCallback(() => {
    setSlideDirection('next');
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    setIsAutoPlaying(false);
    // Resume auto-play after 3 seconds
    setTimeout(() => setIsAutoPlaying(true), 3000);
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [goToNext, goToPrevious]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  // removed unused itemVariants

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-clash font-bold mb-6">
            <span className="gradient-text">Featured</span> Work
          </h2>
          <p className="text-xl text-gray-400 font-inter max-w-3xl mx-auto mb-4">
            A collection of projects built to solve real problems, drive results, and create meaningful user experiences.
          </p>
        </motion.div>

        {/* Carousel Container */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative"
        >
          {/* Main Carousel */}
          <div 
            className="relative overflow-hidden rounded-2xl bg-gray-800/50 dark:bg-gray-800/50 bg-white flex items-center justify-center border-2 border-gray-600 dark:border-white"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <div className="slider-container relative w-full flex items-center justify-center h-[500px] md:h-[600px] lg:h-[700px] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ 
                    opacity: 0, 
                    x: slideDirection === 'next' ? 50 : -50, 
                    scale: 0.95 
                  }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ 
                    opacity: 0, 
                    x: slideDirection === 'next' ? -50 : 50, 
                    scale: 0.95 
                  }}
                  transition={{ 
                    duration: 0.5, 
                    ease: [0.4, 0.0, 0.2, 1],
                    opacity: { duration: 0.3 },
                    x: { duration: 0.5 },
                    scale: { duration: 0.4 }
                  }}
                  className="absolute inset-0 w-full flex items-center justify-center"
                >
                  <div className="w-full flex items-center justify-center">
                    <img
                      src={images[currentIndex]?.src}
                      alt={images[currentIndex]?.title}
                      className="max-w-full h-auto max-h-[500px] md:max-h-[600px] lg:max-h-[700px] object-contain"
                      loading="lazy"
                    />
                  </div>
                  
                  {/* Overlay Content */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  />
                  <motion.div 
                    className="absolute bottom-0 left-0 right-0 p-6 md:p-8"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <h3 className="text-3xl md:text-4xl lg:text-5xl font-clash font-bold text-white mb-4">
                      {images[currentIndex]?.title}
                    </h3>
                    <p className="text-gray-300 text-lg md:text-xl mb-6 max-w-3xl">
                      {images[currentIndex]?.description}
                    </p>
                    {images[currentIndex]?.tags?.length > 0 && (
                      <motion.div 
                        className="flex flex-wrap gap-3"
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.3 }}
                      >
                        {images[currentIndex]?.tags.map((tag, tagIndex) => (
                          <motion.span
                            key={tagIndex}
                            className="px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-emerald-400/20 text-cyan-200 text-sm rounded-full border border-cyan-400/40"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.5 + tagIndex * 0.1, duration: 0.2 }}
                          >
                            {tag}
                          </motion.span>
                        ))}
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white text-2xl transition-all duration-300 hover:shadow-lg hover:shadow-black/30"
              data-cursor="pointer"
            >
              ←
            </button>
            
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white text-2xl transition-all duration-300 hover:shadow-lg hover:shadow-black/30"
              data-cursor="pointer"
            >
              →
            </button>

            {/* Progress Indicators */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {images.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setSlideDirection(index > currentIndex ? 'next' : 'prev');
                    setCurrentIndex(index);
                    setIsAutoPlaying(false);
                    setTimeout(() => setIsAutoPlaying(true), 3000);
                  }}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-gradient-to-r from-cyan-500 to-emerald-400' 
                      : 'bg-white/30 dark:bg-white/30 bg-black/30 hover:bg-white/50 dark:hover:bg-white/50 hover:bg-black/50'
                  }`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  animate={{
                    scale: index === currentIndex ? 1.25 : 1,
                    opacity: index === currentIndex ? 1 : 0.6
                  }}
                  transition={{ duration: 0.2 }}
                  data-cursor="pointer"
                />
              ))}
            </div>

            {/* Image Counter */}
            <div className="absolute top-4 right-4 bg-black/50 px-4 py-2 rounded-full text-white text-sm font-inter">
              {currentIndex + 1} / {images.length}
            </div>

            {/* Play/Pause Button */}
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="absolute top-4 left-4 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
              data-cursor="pointer"
            >
              {isAutoPlaying ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          </div>

        </motion.div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="relative max-w-4xl max-h-[90vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-cyan-400 transition-colors"
                data-cursor="pointer"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="bg-gray-900 rounded-2xl overflow-hidden">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-[60vh] object-cover"
                />
                <div className="p-8">
                  <h3 className="text-3xl font-clash font-bold text-white mb-4">
                    {selectedImage.title}
                  </h3>
                  <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                    {selectedImage.description}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {selectedImage.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600/30 to-cyan-400/30 text-blue-200 rounded-full border border-blue-500/50"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Showcase;
